import { Collapsible as BaseCollapsible } from '@base-ui/react/collapsible';
import { ChevronDown } from 'lucide-react';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';
import { Button, type ButtonProps } from './button';
import { Icon } from './icon';

/**
 * 펼치고 접는 한 자리 — Base UI `Collapsible` 위에 얹는다.
 * 패널 높이는 즉시 바뀌고 열릴 때 내용만 opacity로 들어온다 — 높이 전환은 아래 형제를 매 프레임
 * 다시 배치시킨다(D4-21). 비어 있음을 여기 접어 두지 않는다 — 그건 `EmptyState`다.
 * 제어·비제어 모두 Base UI 그대로 통과시킨다.
 */
export const DisclosureRoot = BaseCollapsible.Root;

export interface DisclosureTriggerProps extends ComponentProps<typeof BaseCollapsible.Trigger> {
  size?: ButtonProps['size'];
}

/**
 * 기본 렌더는 ghost 버튼 + 여닫힘을 따라 도는 chevron이다.
 * `render`를 주면(다른 Base UI trigger와 같은 방식) 그 요소에 접근성 속성만 얹는다.
 */
export function DisclosureTrigger({
  className,
  children,
  render,
  size = 'sm',
  ...props
}: DisclosureTriggerProps) {
  return (
    <BaseCollapsible.Trigger
      render={
        render ?? (
          <Button variant="ghost" size={size} className={cn('group gap-tight', className)}>
            {children}
            <Icon
              as={ChevronDown}
              size="sm"
              className="ease-flow duration-fast group-data-[panel-open]:rotate-180 motion-safe:transition-[rotate]"
            />
          </Button>
        )
      }
      {...props}
    />
  );
}

export function DisclosurePanel({
  className,
  ...props
}: ComponentProps<typeof BaseCollapsible.Panel>) {
  return (
    <BaseCollapsible.Panel
      className={cn(
        'ease-flow duration-fast transition-opacity data-[starting-style]:opacity-0',
        className,
      )}
      {...props}
    />
  );
}

export const Disclosure = {
  Root: DisclosureRoot,
  Trigger: DisclosureTrigger,
  Panel: DisclosurePanel,
};
